import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import type {
  AssistantScreen,
  AssistantTurnRequest,
  Proposal,
  ProposalKind,
} from "./types"

/**
 * What a screen does with a proposal of one kind. `review` opens it in the
 * screen's own UI (a diff, the template wizard, the draft); the screen sets
 * the proposal's status in the assistant store when the user decides.
 * `isStale` says the proposal no longer matches what is open (Review is then
 * off). `blockedReason` is a reason Review is off for now (e.g. a script to
 * save first), or null. `canUndo` says an accepted proposal can still be
 * taken back, which `undo` does (the card then offers Undo).
 */
export interface ProposalHandler {
  review(p: Proposal): void
  isStale?(p: Proposal): boolean
  blockedReason?(p: Proposal): string | null
  canUndo?(p: Proposal): boolean
  undo?(p: Proposal): void
}

/** What the open screen tells the assistant: what it is, what it shows, what it can review. */
export interface AssistantScreenRegistration {
  screen: AssistantScreen
  /** Read when a turn is sent, so it is always the current state. */
  getContext(): AssistantTurnRequest["context"]
  handlers: Partial<Record<ProposalKind, ProposalHandler>>
  /**
   * Changes (by `Object.is`) when what the handlers answer may have changed
   * (the open document was edited, a list they check arrived), so the
   * proposal cards ask them again. The handlers are read when a card
   * renders, and nothing else makes the cards render again.
   */
  revision?: unknown
}

interface AssistantScreenContextValue {
  projectId: string
  owner: string
  registration: AssistantScreenRegistration | null
  register: React.Dispatch<
    React.SetStateAction<AssistantScreenRegistration | null>
  >
}

const AssistantScreenContext = createContext<AssistantScreenContextValue | null>(
  null
)

/**
 * Holds the project the assistant works in and the registration of the
 * screen open inside it. `AssistantDock` provides it; a screen that places
 * the panel itself (the editor) wraps its page in it.
 */
export const AssistantScreenProvider: React.FC<{
  projectId: string
  owner: string
  children?: React.ReactNode
}> = ({ projectId, owner, children }) => {
  const [registration, register] = useState<AssistantScreenRegistration | null>(
    null
  )
  const value = useMemo(
    () => ({ projectId, owner, registration, register }),
    [projectId, owner, registration]
  )
  return (
    <AssistantScreenContext.Provider value={value}>
      {children}
    </AssistantScreenContext.Provider>
  )
}

/** The provider's value; throws outside an `AssistantScreenProvider`. */
export const useAssistantScreen = (): AssistantScreenContextValue => {
  const value = useContext(AssistantScreenContext)
  if (!value) {
    throw new Error("useAssistantScreen needs an AssistantScreenProvider")
  }
  return value
}

/**
 * The provider's value, or null outside an `AssistantScreenProvider` (for
 * shared parts, like the editor's toolbar, that are also used without one).
 */
export const useOptionalAssistantScreen = (): AssistantScreenContextValue | null =>
  useContext(AssistantScreenContext)

/**
 * Registers the calling screen with the assistant while it is mounted. The
 * registration may be a new object on every render: `getContext` and the
 * handlers are always read from the latest one, and the screen registers
 * again only when its name, the kinds it handles or its `revision` change.
 */
export const useRegisterAssistantScreen = (
  reg: AssistantScreenRegistration
): void => {
  const { register } = useAssistantScreen()
  const latest = useRef(reg)
  useEffect(() => {
    latest.current = reg
  })
  const kinds = (Object.keys(reg.handlers) as ProposalKind[])
    .filter((k) => reg.handlers[k])
    .sort()
    .join(",")

  useEffect(() => {
    const handlers: AssistantScreenRegistration["handlers"] = {}
    for (const kind of kinds.split(",").filter(Boolean) as ProposalKind[]) {
      const current = (): ProposalHandler | undefined =>
        latest.current.handlers[kind]
      handlers[kind] = {
        review: (p) => current()?.review(p),
        isStale: (p) => current()?.isStale?.(p) ?? false,
        blockedReason: (p) => current()?.blockedReason?.(p) ?? null,
        canUndo: (p) => current()?.canUndo?.(p) ?? false,
        undo: (p) => current()?.undo?.(p),
      }
    }
    const registered: AssistantScreenRegistration = {
      screen: reg.screen,
      getContext: () => latest.current.getContext(),
      handlers,
    }
    register(registered)
    return () => register((r) => (r === registered ? null : r))
    // A new revision registers again, so the cards render again.
  }, [register, reg.screen, kinds, reg.revision])
}

/**
 * Registers `registration` while mounted: `useRegisterAssistantScreen` as a
 * component, for a screen that provides the `AssistantScreenProvider` itself
 * (the editor) and so cannot call the hook above it.
 */
export const AssistantScreenRegistrar: React.FC<{
  registration: AssistantScreenRegistration
}> = ({ registration }) => {
  useRegisterAssistantScreen(registration)
  return null
}
